import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';

import { Iconify } from 'src/components/iconify';
import {
  navItemStyles,
  navSectionClasses,
  createNavItem as createNavItemBase,
} from 'src/components/nav-section';

// ----------------------------------------------------------------------

// createNavItem 은 아직 JS 라 인자 전부가 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const createNavItem = createNavItemBase as unknown as (args: {
  path?: string;
  icon?: React.ReactNode;
  hasChild?: boolean;
  externalLink?: boolean;
}) => { baseProps: Record<string, unknown>; renderIcon?: React.ReactNode; renderInfo?: React.ReactNode };

type NavItemStateProps = {
  open?: boolean;
  active?: boolean;
};

type NavItemProps = React.ComponentProps<typeof ButtonBase> &
  NavItemStateProps & {
    path?: string;
    icon?: React.ReactNode;
    title?: React.ReactNode;
    hasChild?: boolean;
    externalLink?: boolean;
  };

export function NavItem({
  path,
  icon,
  title,
  /********/
  open,
  active,
  /********/
  hasChild,
  className,
  externalLink,
  ...other
}: NavItemProps) {
  const navItem = createNavItem({
    path,
    icon,
    hasChild,
    externalLink,
  });

  const ownerState = { open, active };

  return (
    <ItemRoot
      aria-label={title}
      {...ownerState}
      {...navItem.baseProps}
      className={mergeClasses([navSectionClasses.item.root, className], {
        [navSectionClasses.state.open]: open,
        [navSectionClasses.state.active]: active,
      })}
      {...other}
    >
      {/*
        ⛔ **그림을 두지 않는다** — 넉 줄짜리 메뉴에 그림을 달면 글자보다 그림이 먼저 읽히고,
          서비스 안의 왼쪽 메뉴(그림 없는 글자 줄)와 다른 제품처럼 보인다.
      */}
      <ItemTitle {...ownerState}>{title}</ItemTitle>

      {hasChild && (
        <ItemArrow
          {...ownerState}
          icon={open ? 'solar:alt-arrow-down-linear' : 'solar:alt-arrow-right-linear'}
        />
      )}
    </ItemRoot>
  );
}

// ----------------------------------------------------------------------

const shouldForwardProp = (prop: string) => !['open', 'active', 'sx'].includes(prop);

/**
 * @slot root
 */
const ItemRoot = styled(ButtonBase, { shouldForwardProp })<NavItemStateProps>(({ theme }) => {
  const openStyles = {
    color: theme.vars.palette.text.primary,
    backgroundColor: theme.vars.palette.action.hover,
  };

  /**
   * 고른 줄 — **서비스 왼쪽 메뉴와 같은 결**이다(`components/nav-section/vertical`):
   * 알약으로 채우지 않고 **연한 바탕 + 글자만 파랗게**. 채워 버리면 랜딩 서랍만 유난히 진해
   * 같은 제품으로 안 읽힌다.
   */
  const activeStyles = {
    color: theme.vars.palette.primary.main,
    backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.08),
    '&:hover': { backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.16) },
  };

  /**
   * 🔑 **치수는 서비스 왼쪽 메뉴의 값 그대로**다 — 36px 높이 · 좌 10 / 우 8 여백 ·
   *   `--nav-item-radius`(테마 모서리). 저쪽이 바뀌면 여기도 같이 고친다.
   */
  return {
    height: 36,
    width: '100%',
    marginBottom: 2,
    paddingLeft: '10px',
    paddingRight: '8px',
    borderRadius: `${theme.shape.borderRadius}px`,
    justifyContent: 'flex-start',
    color: theme.vars.palette.common.black,
    variants: [
      { props: { open: true }, style: openStyles },
      { props: { active: true }, style: activeStyles },
    ],
  };
});

/**
 * @slot title
 */
const ItemTitle = styled('span', { shouldForwardProp })<NavItemStateProps>(({ theme }) => ({
  ...navItemStyles.title(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  color: 'black',
  variants: [
    // fontWeightSemiBold 는 테마(core/typography.js)엔 실재하나 mui 증강 타입에 빠져 있다 — 국소 좁힘 (로직 무변)
    { props: { active: true }, style: { fontWeight: (theme.typography as { fontWeightSemiBold?: string }).fontWeightSemiBold, color: 'inherit' } },
  ],
}));

/**
 * @slot arrow
 */
const ItemArrow = styled(Iconify, { shouldForwardProp })(({ theme }) => ({
  ...navItemStyles.arrow(theme),
}));
